import React, { useEffect, useRef, useState } from 'react';
import { fmt } from '../../data/metrics';
import {
  RIBBON_PAD_L,
  RIBBON_PAD_R,
  RIBBON_PLOT_W,
  ribbonPlotWidth,
  ribbonTimeFrac,
  ribbonXAtHour,
} from '../../lib/ribbonTimeAxis';
import { positionShifts } from '../../lib/shiftOverlay';
import type { ForecastData, ServiceTimelineShift } from '../../types';
import { Delta } from '../shared/Delta';
import { ShiftOverlay } from '../shared/ShiftOverlay';
import { smoothPath } from '../shared/smoothPath';
import { niceCeil } from '../../lib/niceScale';

export { niceCeil };

export function pointGuests(p: { guests?: number; covers: number }): number {
  return p.guests ?? p.covers ?? 0;
}

export const fmtGuests = (v: number) => {
  if (v >= 1000) return fmt.num(Math.round(v));
  return Number.isInteger(v) ? String(v) : v.toFixed(0);
};

export function vsBenchmarkPct(
  current: number,
  baseline: number | null | undefined,
): number | null {
  if (baseline == null || baseline === 0) return null;
  return +(((current - baseline) / Math.abs(baseline)) * 100).toFixed(1);
}

export function RibBenchmark({
  baseline,
  current,
  formatValue,
  invert = false,
  deltaMode = 'pct',
  deltaSuffix,
}: {
  baseline: number | null | undefined;
  current: number;
  formatValue: (v: number) => string;
  invert?: boolean;
  deltaMode?: 'pct' | 'min';
  deltaSuffix?: string;
}) {
  if (baseline == null) return null;

  const delta =
    deltaMode === 'min'
      ? Math.round(current - baseline)
      : vsBenchmarkPct(current, baseline);

  return (
    <span className="rib-bench">
      <span className="rib-bench-val">avg {formatValue(baseline)}</span>
      {delta != null && (
        <Delta
          value={delta}
          suffix={deltaSuffix ?? (deltaMode === 'min' ? 'm' : '%')}
          invert={invert}
          size={11}
        />
      )}
    </span>
  );
}

export function usePlotBox(compact: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  const fallbackH = compact ? 48 : 156;
  const [box, setBox] = useState({ w: RIBBON_PLOT_W, h: fallbackH });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const w = Math.round(el.clientWidth);
      const h = Math.round(el.clientHeight);
      if (w < 8 || h < 8) return;
      setBox(prev => (prev.w === w && prev.h === h ? prev : { w, h }));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [compact, fallbackH]);

  return { ref, w: box.w, h: box.h };
}

export function TimelineBand({
  fc,
  nowGuests,
  benchGuests,
  shifts = [],
  compact = false,
}: {
  fc: ForecastData;
  nowGuests: number;
  benchGuests?: number | null;
  shifts?: ServiceTimelineShift[];
  compact?: boolean;
}) {
  const { ref: hostRef, w: W, h: H } = usePlotBox(compact);
  const padL = RIBBON_PAD_L;
  const padR = RIBBON_PAD_R;
  const padT = compact ? 4 : 18;
  const padB = compact ? 4 : 22;
  const pts = fc.pts;
  const hostClass = `rib-band-host${compact ? ' compact' : ''}`;
  if (pts.length < 2) {
    return (
      <div ref={hostRef} className={hostClass}>
        <svg
          className={`rib-band${compact ? ' compact' : ''}`}
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="xMidYMid meet"
        >
          <text x={W / 2} y={H / 2} textAnchor="middle" fill="var(--ink-3)" fontSize="12">
            No guest timeline yet
          </text>
        </svg>
      </div>
    );
  }
  const plotH = H - padT - padB;
  const plotW = ribbonPlotWidth(padL, padR, W);
  const rawMax = Math.max(
    ...pts.map(pointGuests),
    pointGuests(fc.peak),
    nowGuests,
    1,
  );
  const yMax = niceCeil(rawMax * 1.08);
  const xAtH = (h: number) => ribbonXAtHour(h, fc.openH, fc.closeH, padL, padR, W);
  const yAt = (guests: number) =>
    padT + (1 - Math.min(guests, yMax) / yMax) * plotH;
  const coords = pts.map(
    p => [xAtH(p.h), yAt(pointGuests(p))] as [number, number],
  );
  const line = smoothPath(coords);
  const area = `${line} L${xAtH(pts[pts.length - 1].h).toFixed(1)} ${H - padB} L${padL} ${H - padB} Z`;
  const nowFrac = ribbonTimeFrac(fc.nowH, fc.openH, fc.closeH);
  const nowX = padL + nowFrac * plotW;
  const peakI = pts.findIndex(p => p.h === fc.peak.h);
  const nowInWindow = fc.nowH >= fc.openH && fc.nowH <= fc.closeH;
  const curveNowGuests = nowGuests;
  const nowY = yAt(curveNowGuests);
  const yTicks = compact ? [yMax] : [0, yMax / 2, yMax];
  const uid = `rib${compact ? 'c' : 'f'}${Math.round(fc.projectedCovers)}`;
  const pillW = Math.max(
    compact ? 22 : 28,
    fmtGuests(curveNowGuests).length * (compact ? 6 : 7) + (compact ? 8 : 10),
  );
  const strokeBooked = compact ? 1.5 : 2.2;
  const strokeFact = compact ? 1.7 : 2.4;
  const hourGap = pts.length > 1 ? plotW / (pts.length - 1) : plotW;
  const hourStep = hourGap < 40 ? 2 : 1;
  const positionedShifts = positionShifts(
    shifts,
    fc.openH,
    fc.closeH,
    padL,
    padR,
    W,
  );
  return (
    <div ref={hostRef} className={hostClass}>
      <svg
        className={`rib-band${compact ? ' compact' : ''}`}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
      >
      <defs>
        <linearGradient id={`${uid}g`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="var(--accent)" stopOpacity="0.20" />
          <stop offset="1" stopColor="var(--accent)" stopOpacity="0" />
        </linearGradient>
        <clipPath id={`${uid}past`}>
          <rect x="0" y="0" width={nowX} height={H} />
        </clipPath>
        <clipPath id={`${uid}future`}>
          <rect x={nowX} y="0" width={W - nowX} height={H} />
        </clipPath>
      </defs>
      <ShiftOverlay
        shifts={positionedShifts}
        y0={0}
        y1={H - padB}
        showLabels={!compact}
        labelY={compact ? padT + 8 : padT + 2}
      />
      <rect
        x={nowX}
        y="0"
        width={W - nowX}
        height={H - padB}
        fill="var(--ink-3)"
        opacity="0.05"
      />
      {yTicks.map((t, i) => {
        const y = yAt(t);
        const isBaseline = !compact && i === 0;
        return (
          <g key={i}>
            <line
              x1={padL}
              x2={W - padR}
              y1={y}
              y2={y}
              stroke="var(--line)"
              strokeWidth="1"
              strokeDasharray={isBaseline ? undefined : '2 3'}
              opacity={isBaseline ? 1 : 0.7}
            />
            <text
              x={padL - 5}
              y={compact ? padT + 9 : y + 3}
              fill="var(--ink-3)"
              fontSize={compact ? '9' : '11.5'}
              textAnchor="end"
              className="rib-tick"
            >
              {fmtGuests(t)}
            </text>
          </g>
        );
      })}
      {peakI >= 0 && (
        <path d={area} fill={`url(#${uid}g)`} clipPath={`url(#${uid}past)`} />
      )}
      <path
        d={line}
        fill="none"
        stroke="var(--petrol-ink)"
        strokeWidth={strokeBooked}
        strokeLinecap="round"
        clipPath={`url(#${uid}future)`}
      />
      <path
        d={line}
        fill="none"
        stroke="var(--accent)"
        strokeWidth={strokeFact}
        strokeLinecap="round"
        clipPath={`url(#${uid}past)`}
      />
      {peakI >= 0 && (
        <circle
          cx={coords[peakI][0]}
          cy={coords[peakI][1]}
          r={compact ? 2.4 : 3.4}
          fill="var(--accent)"
          stroke="var(--surface)"
          strokeWidth={compact ? 1.4 : 2}
        />
      )}
      {nowInWindow && (
        <>
          {!compact && benchGuests != null && benchGuests > 0 && (
            <line
              x1={padL}
              x2={W - padR}
              y1={yAt(benchGuests)}
              y2={yAt(benchGuests)}
              stroke="var(--ink-3)"
              strokeWidth="1"
              strokeDasharray="3 4"
              opacity="0.55"
            />
          )}
          <line
            x1={nowX}
            x2={nowX}
            y1={compact ? padT : padT - 8}
            y2={H - padB}
            stroke="var(--accent)"
            strokeWidth={compact ? 1.1 : 1.4}
          />
          <circle
            cx={nowX}
            cy={nowY}
            r={compact ? 2.4 : 3.6}
            fill="var(--surface)"
            stroke="var(--accent)"
            strokeWidth={compact ? 1.6 : 2.4}
          />
          {!compact && (
            <g
              transform={`translate(${Math.max(
                padL + pillW / 2,
                Math.min(W - padR - pillW / 2, nowX),
              )}, ${padT - 8})`}
            >
              <rect
                x={-pillW / 2}
                y="-9"
                width={pillW}
                height="14"
                rx="7"
                fill="var(--accent)"
              />
              <text
                x="0"
                y="1"
                fill="#fff"
                fontSize="11.5"
                fontWeight="700"
                textAnchor="middle"
                dominantBaseline="middle"
                className="rib-tick"
              >
                {fmtGuests(curveNowGuests)}
              </text>
            </g>
          )}
        </>
      )}
      {!compact &&
        pts.map((p, i) => {
          const last = i === pts.length - 1;
          if (hourStep > 1 && i % hourStep !== 0 && !last && i !== 0) return null;
          return (
            <text
              key={p.h}
              x={Math.max(padL + 8, Math.min(W - padR - 8, xAtH(p.h)))}
              y={H - 6}
              fill="var(--ink-3)"
              fontSize="11"
              textAnchor="middle"
              className="rib-tick"
            >
              {p.h % 24}h
            </text>
          );
        })}
      </svg>
    </div>
  );
}

export function RibStat({
  value,
  label,
  tense,
  benchmark,
  onClick,
}: {
  value: React.ReactNode;
  label: React.ReactNode;
  tense: string;
  benchmark?: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <div
      className={`rib-stat${onClick ? ' click' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
    >
      <span className={`tdot ${tense}`} />
      <div className="rib-stat-body">
        <div className="rib-stat-v-row">
          <span className="rib-stat-v">{value}</span>
          {benchmark}
        </div>
        <span className="rib-stat-l">{label}</span>
      </div>
    </div>
  );
}
